import type { GrowCardData } from "./grow-card";

/**
 * The carousel's shared types and its development diagnostic.
 *
 * ⚠ THIS MODULE IS ON THE CLIENT EDGE — carousel-shell.client.tsx imports
 * `warnInDevelopment` from it, so anything added here ships to the browser.
 * The card contract and its producers live in `card-contract.server.ts` for
 * exactly that reason.
 */

/**
 * One authored card as the CAROUSEL reads it. Exactly the card shape today,
 * kept as the carousel's own name for the boundary so a later section-only
 * field has somewhere to land.
 */
export type GrowCarouselCard = GrowCardData;

/**
 * A switch as Storyblok PUBLISHES it, not as the schema describes it: the
 * boolean, or the string "true"/"false". Declared because `isOff` already
 * reads all three, and a prop typed `boolean` while holding "false" makes a
 * later `if (showTabs)` type-check and read an off switch as on (WAT-12200).
 */
export type StoryblokSwitch = boolean | "true" | "false";

/* Per-field refusal cost — see REFUSAL_COST below. */
export type PaintField = "accent" | "accent_deep";

/* WAT-12188: the pagination bar IS accent-driven — `.root .glowBar` re-points
   the atom's --bar-color at --gcc-accent — so a refused accent costs the bar
   as well as the backdrop. `accent_deep` costs nothing that renders; it stays
   published because `Bloom` still sets it and retiring it is an AC change
   (WAT-11671 open question 3), not a refactor. */
const REFUSAL_COST: Record<PaintField, string> = {
  accent:
    "the backdrop bloom and the pagination bar both fall back to the " +
    "default accent",
  accent_deep:
    "nothing visible changes today — no shipped rule reads --gcc-accent-deep, " +
    "and the pagination bar follows --gcc-accent rather than its deeper step"
};

/* ⚠ No blank-accessible-name branch belongs here. Every landmark the section
   publishes — the tab tray, the rail's group and the pagination <nav> —
   resolves its own name in carousel-shell.client.tsx against a constant held
   beside the other four, so `=== ""` is unreachable for all three and a
   diagnostic guarding it can only mislead (WAT-12200). */
export type DevelopmentWarningInput = {
  cards: GrowCardData[];
  authored: number;
  unresolved: string[];
  refusedPaints: PaintField[];
  /** Each already rendered as `field "value"` — see the section's own guard. */
  refusedVariants: string[];
  /** Diagnostics already emitted by this instance, keyed by message. */
  seen: Set<string>;
};

/* Development-only. Every branch reports a section that renders a good picture
   with something only a screen reader would miss. */
export function warnInDevelopment({
  cards,
  authored,
  unresolved,
  refusedPaints,
  refusedVariants,
  seen
}: DevelopmentWarningInput) {
  const once = (message: string) => {
    if (seen.has(message)) {
      return;
    }
    seen.add(message);
    console.warn(message);
  };

  if (cards.length === 0) {
    once(
      "[GrowCardCarousel] no renderable cards, so the section renders nothing at " +
        "all. Check the authored list reached the component and that every entry " +
        "carries an `id`."
    );
  } else if (cards.length !== authored) {
    once(
      `[GrowCardCarousel] dropped ${authored - cards.length} card(s) with a ` +
        "missing or repeated `id`. The id is the React key and the tab value, " +
        "so a rail cannot hold two cards under one."
    );
  }

  if (unresolved.length > 0) {
    /* Deduped HERE, not at the point a field is pushed: `unresolved` is one
       array shared across the heading, the subheading and every card's
       `cardLabel` (grow-card-carousel.tsx), so the same field name — e.g.
       `tab_label` — can be pushed once per card. */
    once(
      `[GrowCardCarousel] suppressed ${[...new Set(unresolved)].join(", ")} because ` +
        "the value still holds an unresolved [<TOKEN>], which must never reach a " +
        "customer. Dynamic text is resolved server-side before it gets here, so " +
        "this is an authoring or a resolution gap rather than a component problem."
    );
  }

  /* FORMAT only — `paint()` refuses nothing else. A well-formed accent the
     rail's focus ring cannot be seen over is refused on the server and reports
     itself there, in its own wording (ring-legibility.server.ts). */
  for (const field of refusedPaints) {
    once(
      `[GrowCardCarousel] refused a card's ${field} because the value is not a ` +
        `colour, so ${REFUSAL_COST[field]} and the pack loses that colour. ` +
        "Author a hex colour (#680dab) or a PUBLISHED palette step " +
        "(amethyst-600) — a step the token layer does not publish resolves to " +
        "nothing, and anything else can pull arbitrary CSS into the section."
    );
  }

  /* The heading still renders — it just stops honouring the variant, which is
     the one failure a screenshot of a good-looking section does not show. */
  for (const refusal of refusedVariants) {
    once(
      `[GrowCardCarousel] refused ${refusal}, so that line falls back to the ` +
        "default heading/subheading style (headline-2 / subhead-2). The heading " +
        "takes a heading step and the subheading a body step, so a published " +
        "Text step outside the field's own set — a number or text-link step — " +
        "is refused here too."
    );
  }
}
